/**
 * **Filling one in** (ADR 0034, James Oct 14 — Forms.dc.html `fill`): a small form's own page at
 * /f/<token>. Apple Fest's frame only — no account, no CIDER — and one question at a time, big
 * enough for a phone. Send is one tap; the answer reaches the person who asked, and nothing
 * else is sent.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Field, Input, Select } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { LoginFrame } from '../login/Login.js';

interface Question {
  key: string;
  kind: 'short' | 'long' | 'one' | 'many' | 'number' | 'date';
  label: string;
  required: boolean;
  choices?: string[];
}
interface FormView {
  title: string;
  fields: Question[];
  open: boolean;
  endsOn: string;
}

export function FormFill({ token }: { token: string }): ReactNode {
  const [form, setForm] = useState<FormView | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    api<FormView>('GET', `/public/forms/${token}`)
      .then(setForm)
      .catch((e: unknown) => {
        setDead(sentence(e));
      });
  }, [token]);

  if (dead || (form && !form.open))
    return (
      <LoginFrame>
        <section className="login__card2" data-screen="form">
          <div className="login__eyebrow">SAINT LAWRENCE APPLE FEST</div>
          <h2>{form ? form.title : 'This link is not live'}</h2>
          <p role="alert">{dead ?? 'This form has closed. Thank you all the same.'}</p>
        </section>
      </LoginFrame>
    );
  if (!form) return <div className="boot">Opening the form…</div>;

  const q = form.fields[step];
  const last = step === form.fields.length - 1;
  const value = q ? answers[q.key] : undefined;
  const set = (v: string | string[]): void => {
    if (q) setAnswers({ ...answers, [q.key]: v });
  };
  const send = (): void => {
    setBusy(true);
    setRefusal(null);
    api<{ says: string }>('POST', `/public/forms/${token}`, { answers })
      .then((r) => {
        setDone(r.says);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <LoginFrame>
      <section className="login__card2" data-screen="form" aria-label={form.title}>
        <div className="login__eyebrow">SAINT LAWRENCE APPLE FEST</div>
        <h2>{form.title}</h2>
        {done ? (
          <p role="status">{done}</p>
        ) : q ? (
          <>
            <p className="ui-table__sub">{`${String(step + 1)} of ${String(form.fields.length)}`}</p>
            {q.kind === 'one' ? (
              <Field id="ff-q" label={q.label}>
                <Select
                  id="ff-q"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => {
                    set(e.target.value);
                  }}
                >
                  <option value="">Choose one</option>
                  {(q.choices ?? []).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : q.kind === 'many' ? (
              <fieldset className="ui-field">
                <legend>{q.label}</legend>
                {(q.choices ?? []).map((c) => {
                  const picked = Array.isArray(value) ? value : [];
                  return (
                    <label key={c} className="ui-check">
                      <input
                        type="checkbox"
                        checked={picked.includes(c)}
                        onChange={(e) => {
                          set(e.target.checked ? [...picked, c] : picked.filter((x) => x !== c));
                        }}
                      />{' '}
                      {c}
                    </label>
                  );
                })}
              </fieldset>
            ) : q.kind === 'long' ? (
              <Field id="ff-q" label={q.label}>
                <textarea
                  id="ff-q"
                  className="ui-input"
                  rows={5}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => {
                    set(e.target.value);
                  }}
                />
              </Field>
            ) : (
              <Field id="ff-q" label={q.label}>
                <Input
                  id="ff-q"
                  type={q.kind === 'date' ? 'date' : 'text'}
                  inputMode={q.kind === 'number' ? 'decimal' : undefined}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => {
                    set(e.target.value);
                  }}
                />
              </Field>
            )}
            {refusal ? (
              <p className="ui-form__refusal" role="alert">
                {refusal}
              </p>
            ) : null}
            <span className="ui-row">
              {step > 0 ? (
                <Button
                  onClick={() => {
                    setStep(step - 1);
                  }}
                >
                  Back
                </Button>
              ) : null}
              {last ? (
                <Button kind="primary" busy={busy} onClick={send}>
                  Send
                </Button>
              ) : (
                <Button
                  kind="primary"
                  onClick={() => {
                    setStep(step + 1);
                  }}
                >
                  Next
                </Button>
              )}
            </span>
          </>
        ) : null}
      </section>
    </LoginFrame>
  );
}
